import { useLazyQuery, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  Text,
  FormErrorMessage,
  FormLabel,
  Heading,
  Spinner,
  useRadioGroup,
  useToast,
  VStack,
  Link,
} from "@chakra-ui/react";
import { useCallback, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { BsCart4 } from "react-icons/bs";
import { FiHome } from "react-icons/fi";
import { useNavigate } from "react-router";
import { IAddress } from "../../services/graphql/Address";
import { IProductsWithoutStock } from "../../services/graphql/Cart";
import { ADDRESSES } from "../../services/graphql/query/Addresses";
import { CHECK_STOCK } from "../../services/graphql/query/Cart";
import { useCheckUser } from "../../services/hooks/useCheckUser";
import { useUnauthorizedAction } from "../../services/hooks/useUnauthorizedAction";
import { ContextApp } from "../../shared/ContextApp";
import { MessageError } from "../../shared/MessageError";
import { MercadoPago } from "../MercadoPago/MercadoPago";
import { RadioCard } from "./RadioCard";

export const Pay = () => {
  useCheckUser();
  const [loading, setLoading] = useState(true);
  const [addresses, setAddresses] = useState<IAddress[]>([]);
  const { addressIdForBuy, setAddressIdForBuy } = useContext(ContextApp);
  const [addressId, setaddressId] = useState<number>();
  const [radioValue, setRadioValue] = useState<any>();
  const [productsWithoutStock, setProductsWithoutStock] =
    useState<IProductsWithoutStock>({
      product: [],
    });
  const toast = useToast();
  const { action } = useUnauthorizedAction();
  const unauthorized = useCallback(() => {
    action();
  }, []);
  const [getAddresses] = useLazyQuery(ADDRESSES);

  useQuery(CHECK_STOCK, {
    onCompleted: (dataAddresses) => {
      const { checkStock } = dataAddresses;
      setProductsWithoutStock(checkStock);
      getAddresses()
        .then((dataAddresses) => {
          const { data } = dataAddresses;
          const { addresses } = data;
          setAddresses(addresses);
          setLoading(false);
        })
        .catch((error) => {
          if (error.message !== "Unauthorized") {
            toast({
              duration: 2500,
              position: "top",
              render: () =>
                MessageError(
                  error.message
                    ? error.message
                    : "Error al recuperar las ciudades."
                ),
            });
          } else {
            unauthorized();
          }
          setLoading(false);
        });
    },
    onError: (error) => {
      if (error.message !== "Unauthorized") {
        toast({
          duration: 2500,
          position: "top",
          render: () =>
            MessageError(
              error.message ? error.message : "No pudimos chequear el stock."
            ),
        });
      } else {
        unauthorized();
      }
      setLoading(false);
    },
  });

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: "address_id",
    onChange: (value) => {
      setRadioValue(value);
    },
  });

  const group = getRootProps({
    id: "address_id",
  });
  const navigate = useNavigate();
  const onSubmit = () => {
    if (!radioValue) {
      toast({
        duration: 2500,
        position: "top",
        render: () => MessageError("Debe seleccionar una dirección"),
      });
      return;
    }
    setaddressId(Number(radioValue));
    localStorage.setItem("address_id", radioValue);
  };

  return (
    <>
      {loading ? (
        <Center>
          <Spinner />
        </Center>
      ) : (
        <>
          {productsWithoutStock?.product.length > 0 ? (
            <Center>
              <Flex flexDirection={"column"}>
                <Box textAlign={"center"}>
                  <Text fontWeight={"bold"}>
                    {productsWithoutStock?.product.length === 1
                      ? "No tenemos stock para el siguiente producto"
                      : "No tenemos stock para los siguientes productos"}
                  </Text>
                  {productsWithoutStock?.product.map((product) => (
                    <Text>{product.name}</Text>
                  ))}
                </Box>
                <Box textAlign={"center"} mt={5}>
                  <Text fontWeight={"bold"}>
                    Por favor revisa tu{" "}
                    <Link href="/cart" textDecoration={"underline"}>
                      carrito
                    </Link>
                  </Text>
                </Box>
              </Flex>
            </Center>
          ) : (
            <>
              {!addressId ? (
                <>
                  <Center>
                    <Box minW="200px" maxW={"500px"} w="100%">
                      <form
                        autoComplete="off"
                        onSubmit={(e) => {
                          e.preventDefault();
                          onSubmit();
                        }}
                      >
                        <FormControl>
                          <FormLabel>
                            Seleccione una dirección para que le enviemos el
                            producto
                          </FormLabel>
                          <VStack {...group}>
                            {addresses?.map((value, index) => {
                              const radio = getRadioProps({
                                value: value.id.toString(),
                              });

                              return (
                                <RadioCard key={value} {...radio}>
                                  <span>
                                    {value.street_name} {value.street_number}
                                  </span>{" "}
                                  {value.floor || value.department ? (
                                    <span>
                                      - {value.floor ? value.floor : null}{" "}
                                      {value.department
                                        ? value.department
                                        : null}
                                    </span>
                                  ) : null}
                                </RadioCard>
                              );
                            })}
                          </VStack>
                          <Text>
                            Para administrar tus direcciones hace click{" "}
                            <Link
                              href="/address"
                              style={{ textDecoration: "underline" }}
                            >
                              aquí
                            </Link>
                          </Text>
                        </FormControl>

                        <Button type="submit" w={"100%"} mt={4}>
                          Pagar
                        </Button>
                      </form>
                    </Box>
                  </Center>
                </>
              ) : (
                <>
                  <MercadoPago
                    onLoaded={() => setLoading(false)}
                    onError={() => setLoading(false)}
                  />
                  <Center
                    display={"flex"}
                    flexDirection="column"
                    textAlign={"center"}
                  >
                    <Heading mb={3} as="h4">
                      ¿Que desea hacer?
                    </Heading>
                    <Flex flexDirection={{ base: "column", sm: "row" }}>
                      <Button
                        mb={{ base: "10px", sm: "0px" }}
                        mr={{ base: "0px", sm: "10px" }}
                        leftIcon={<FiHome />}
                        onClick={() => {
                          navigate("/");
                        }}
                      >
                        Ir al inicio
                      </Button>
                      <Button
                        leftIcon={<BsCart4 />}
                        onClick={() => {
                          navigate("/cart");
                        }}
                      >
                        Ir al carrito
                      </Button>
                    </Flex>
                  </Center>
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
